import { getSubscriptionInfo } from '~/services/subscriptions';
import { getLocaleForCountry } from '~/services/locale-map';

import {
    ADD_PERSISTENT_NOTIFICATION,
    REMOVE_PERSISTENT_NOTIFICATION,
    SET_COOKIE_BANNER_HIDDEN,
    SET_COUNTRY_GEO,
    SET_ENV_VARS,
    SET_IP_ADDRESS,
    SET_USER_INFO,
    SET_USER_DATA,
    UPDATE_PROFILE_IMAGE,
} from '~/store/mutationTypes';

import {
    ACTION_DETECT_COUNTRY_GEO,
    ACTION_FETCH_USER_INFO,
    ACTION_FETCH_USER_DATA,
} from '~/store/actionTypes';

const COUNTRY_COOKIE = 'app_country';
const DEFAULT_COUNTRY = 'us';
const DEFAULT_LOCALE = 'en-US';

/**
 * @typedef {{
 *   country: string
 *   cookieBannerHidden: boolean
 *   env: {[key: string]: string}
 *   ipAddress: string
 *   userLocale: string
 *   persistentNotifications: TIDAL.Internal.PersistentNotification[]
 *   persistentNotificationsLastId: number
 *   userSubscription: TIDAL.Subscription | TIDAL.FreeTier | null
 *   userData: UserWebApi.UserDataRequestReport | null
 *   userInfo: TIDAL.UserInfo | null
 *   userPrices: TIDAL.SubscriptionPrices | null
 * }} State
 *
 * @typedef {import('vuex').Store<State>} AccountStore
 */

/** @return {AccountStore['state']} */
export const state = () => ({
    country: DEFAULT_COUNTRY,
    cookieBannerHidden: false,
    env: {},
    ipAddress: '',
    userLocale: DEFAULT_LOCALE,
    persistentNotifications: [],
    persistentNotificationsLastId: 0,
    userSubscription: null,
    userData: null,
    userInfo: null,
    userPrices: null,
});

/** @type {import('vuex').MutationTree<State>} */
export const mutations = {
    /**
     * @param {State} state
     * @param {TIDAL.PersistentNotification} notification
     */
    [ADD_PERSISTENT_NOTIFICATION](state, notification) {
        state.persistentNotifications.push({
            ...notification,
            id: ++state.persistentNotificationsLastId,
        });
    },
    [REMOVE_PERSISTENT_NOTIFICATION](state, id) {
        for (const [i, notification] of state.persistentNotifications.entries()) {
            if (notification.id === id) {
                state.persistentNotifications.splice(i, 1);
            }
        }
    },
    [SET_ENV_VARS](state, env) {
        state.env = Object.assign({}, env);
    },
    [SET_IP_ADDRESS](state, ipAddress) {
        state.ipAddress = ipAddress;
    },
    [SET_COOKIE_BANNER_HIDDEN](state) {
        state.cookieBannerHidden = true;
    },
    [SET_COUNTRY_GEO](state, country) {
        state.country = country.toLowerCase();
    },
    /**
     * @param {State} state
     * @param {Pick<State, 'userInfo' | 'userPrices'>} object
     */
    [SET_USER_INFO](state, { userInfo, userPrices }) {
        state.userInfo = userInfo;
        state.userPrices = userPrices;
        state.userLocale = getLocaleForCountry(userInfo?.countryCode || '') || DEFAULT_LOCALE;
        state.userSubscription = userInfo ? getSubscriptionInfo(userInfo) : null;
    },
    [SET_USER_DATA](state, userData) {
        state.userData = userData;
    },
    [UPDATE_PROFILE_IMAGE](state, imageId) {
        if (state.userInfo) {
            state.userInfo.imageId = imageId;
        }
    },
};

/** @type {import('vuex').ActionTree<State, State>} */
export const actions = {
    /** nuxtServerInit is called by Nuxt.js before server-rendering every page */
    async nuxtServerInit({ commit, dispatch }, /** @type {import('@nuxt/types').Context} */{ app, env, req }) {
        commit(SET_ENV_VARS, env);
        commit(SET_IP_ADDRESS, req.ipAddress);

        try {
            await dispatch(ACTION_FETCH_USER_DATA);
        } catch {
            // Ignore
        }
        await dispatch(ACTION_DETECT_COUNTRY_GEO, {
            req,
            $cookies: app.$cookies,
        });
    },
    async [ACTION_FETCH_USER_INFO]({ commit }) {
        /** @type {[TIDAL.UserInfo?, TIDAL.SubscriptionPrices?]} */
        const [userInfo, userPrices] = await Promise.all([
            this.$axios.$get('/user').catch(() => null),
            this.$axios.$get('/user/subscription/prices').catch(() => null),
        ]);
        commit(SET_USER_INFO, { userInfo, userPrices });
    },
    async [ACTION_FETCH_USER_DATA]({ commit }) {
        /** @type {UserWebApi.UserDataRequestReport | null} */
        const userData = await this.$axios.$get('/user/userdata');
        commit(SET_USER_DATA, userData);
    },
    async [ACTION_DETECT_COUNTRY_GEO]({ commit }, { req, $cookies }) {
        /**
         * Determines if valid country.
         *
         * @param {string} code The country code
         * @return {boolean} True if valid country, False otherwise.
         */
        const isValidCountry = code => typeof code === 'string' && code.length === 2;

        let detectedCountry = DEFAULT_COUNTRY;
        const countryCookie = $cookies.get(COUNTRY_COOKIE);

        if (isValidCountry(countryCookie)) {
            detectedCountry = countryCookie;
        } else {
            // UserMiddleware sets session.locale object.
            const country = req.countryCode;

            if (country && isValidCountry(country)) {
                detectedCountry = country;
                $cookies.set(COUNTRY_COOKIE, detectedCountry);
            }
        }

        commit(SET_COUNTRY_GEO, detectedCountry);
    },
};
